import { Store } from './store'
import {
  Location,
  PanelHistoryItem,
  PanelHistoryItemWithDelimiter,
} from './types'
import { HistoryEvent, Listeners } from './listeners'

// ActiveSections

export function pushToSectionsHistory(
  section: string,
  calledByInternals = false,
) {
  const history = Store.getActiveSectionsHistory()
  if (history.peek() !== section) history.push(section)

  if (!calledByInternals) Listeners.notify(HistoryEvent.PUSH)
}

export function popFromSectionsHistory(calledByInternals = false) {
  const history = Store.getActiveSectionsHistory()
  history.pop()

  if (!calledByInternals) Listeners.notify(HistoryEvent.POP)
}

export function replaceInSectionsHistory(
  section: string,
  calledByInternals = false,
) {
  const history = Store.getActiveSectionsHistory()
  history.replace(section)

  if (!calledByInternals) Listeners.notify(HistoryEvent.REPLACE)
}

export function resetSectionsHistory(...initialSections: string[]) {
  const history = Store.getActiveSectionsHistory()
  history.reset(...initialSections)
}

export function removeSectionFromSectionsHistory(
  section: string,
  calledByInternals = false,
) {
  const history = Store.getActiveSectionsHistory()
  history.reset(...history.toArray().filter(sec => sec !== section))

  if (!calledByInternals) Listeners.notify(HistoryEvent.RESET)
}

export function getSectionsHistoryTop() {
  const history = Store.getActiveSectionsHistory()
  return history.peek()
}

export function getSectionsHistory() {
  const history = Store.getActiveSectionsHistory()
  return history.toArray()
}

// Sections

export function pushPanelToSection(section: string, panel: string) {
  const history = Store.getSectionHistory(section)
  const panelToPush = Store.getPanelParent(panel) || panel
  const activeSectionsHistory = Store.getActiveSectionsHistory()

  if (!history) return
  const topSection = getSectionsHistoryTop()

  if (topSection !== section || activeSectionsHistory.peek() !== section) {
    if (history.length > 0) history.push('INIT')
    pushToSectionsHistory(section)
  }

  history.push(panelToPush)
  Listeners.notify(HistoryEvent.PUSH)
}

export function popPanelFromSection(section: string) {
  const history = Store.getSectionHistory(section)
  history?.pop()

  if (history?.peek() === 'INIT' || history?.length === 0) {
    history.pop()
    if (getSectionsHistoryTop() === section) popFromSectionsHistory()
  }

  if (history?.length === 0) removeSectionFromSectionsHistory(section, true)
  Listeners.notify(HistoryEvent.POP)
}

export function replacePanelInSection(section: string, panel: string) {
  const history = Store.getSectionHistory(section)
  const panelToReplace = Store.getPanelParent(panel) || panel

  if (getSectionsHistoryTop() !== section) {
    history?.pop()
    if (history?.length > 0) history?.push('INIT')
    pushToSectionsHistory(section)
  }

  history?.push(panelToReplace)
  Listeners.notify(HistoryEvent.REPLACE)
}

export function resetSection(section: string, ...initialPanels: string[]) {
  Store.getSectionHistory(section)?.reset(...initialPanels)
  Listeners.notify(HistoryEvent.RESET)
}

export function getSectionTop(section: string) {
  return Store.getSectionHistory(section)?.peek()
}

export function getSectionHistory(section: string) {
  return Store.getSectionHistory(section)?.toArray()
}

// Panels

export function pushToPanel(
  panel: string,
  location: Location,
  state?: any,
  dontUpdateSections = false,
) {
  const history = Store.getPanelHistory(panel)
  const parentPanel = Store.getPanelParent(panel)

  if (dontUpdateSections || parentPanel) {
    history.push({ location, state })
    Listeners.notify(HistoryEvent.PUSH)

    return
  }

  const sectionName = Store.getSectionOfPanel(panel)
  const sectionHistory = Store.getSectionHistory(sectionName)
  const activeSectionsHistory = Store.getActiveSectionsHistory()

  if (
    sectionHistory &&
    (sectionHistory.peek() !== panel ||
      activeSectionsHistory.peek() !== sectionName)
  ) {
    if (location !== 'INIT' && history.length !== 0)
      history.push('INIT', { location, state })
    else history.push({ location, state })

    pushPanelToSection(sectionName, panel)
  } else history.push({ location, state })

  Listeners.notify(HistoryEvent.PUSH)
}

export function popFromPanel(panel: string) {
  const history = Store.getPanelHistory(panel)
  const sectionName = Store.getSectionOfPanel(panel)
  const sectionTop = getSectionTop(sectionName)

  history.pop()

  if (history.peek() === 'INIT') {
    history.pop()

    const panelParent = Store.getPanelParent(panel)

    if (sectionTop === panel || sectionTop === panelParent)
      popPanelFromSection(sectionName)
  } else if (history.length === 0 && sectionTop === panel)
    popPanelFromSection(sectionName)
  else Listeners.notify(HistoryEvent.POP)
}

export function replaceInPanel(panel: string, location: Location, state?: any) {
  const history = Store.getPanelHistory(panel)
  if (history.length === 0) {
    pushToPanel(panel, location, state)
    Listeners.notify(HistoryEvent.PUSH)
    return
  }

  const sectionName = Store.getSectionOfPanel(panel)
  const sectionHistory = Store.getSectionHistory(sectionName)
  const activeSectionsHistory = Store.getActiveSectionsHistory()

  if (
    sectionHistory &&
    (activeSectionsHistory.length === 0 ||
      activeSectionsHistory.peek() !== sectionName)
  )
    pushToSectionsHistory(sectionName)

  if (sectionHistory && sectionHistory.peek() !== panel) {
    history.pop()

    if (history.length > 0) history.push('INIT', { location, state })
    else history.push({ location, state })

    pushPanelToSection(sectionName, panel)
    return
  }

  history.replace({ location, state })
  Listeners.notify(HistoryEvent.REPLACE)
}

export function resetPanel(
  panel: string,
  ...initialData: PanelHistoryItemWithDelimiter[]
) {
  const history = Store.getPanelHistory(panel)
  history.reset(...initialData)

  Listeners.notify(HistoryEvent.RESET)
}

export function getPanelTop(panel: string) {
  const history = Store.getPanelHistory(panel)
  const panelTop = history?.peek()

  if (panelTop === 'INIT') {
    const [item] = history.toArray().slice(-2, -1)
    return item as PanelHistoryItem
  }

  return panelTop
}

export function getPanelHistory(panel: string) {
  return Store.getPanelHistory(panel)?.toArray()
}

// Store

export function resetRouterStore(initialPanel?: string) {
  Store.initiate()

  if (initialPanel) {
    const sectionOfPanel = Store.getSectionOfPanel(initialPanel)
    resetSection(sectionOfPanel, initialPanel)
  }

  Listeners.notify(HistoryEvent.RESET)
}

// Listener

export const listen = Listeners.listen
